import { useMemo } from 'react';
import { useApiQuery } from '../../../../common/infrastructure/hooks/useApiQuery';
import { endpointsMetadata } from '../../../../common/infrastructure/routes/endpointsMetadata';
import { IWorkingHoursData } from '../../../../common/components/common/workingHoursDialog/models/models';

/**
 * Hook to fetch and process studio working hours data
 * @param studioId - The studio ID to fetch working hours for
 */
export const useStudioWorkingHours = (studioId: number | undefined) => {
  const { data: workingHoursData, isLoading, error } = useApiQuery<IWorkingHoursData>({
    queryKey: [endpointsMetadata.workingHours.getStudioWorkingHours, studioId],
    endpoint: `${endpointsMetadata.workingHours.getStudioWorkingHours}/${studioId}`,
    enabled: !!studioId,
    globalIsLoading: false,
  });

  // Convert working hours data to a more convenient format
  const studioHours = useMemo(() => {
    if (!workingHoursData?.days) {
      return null; // No mock data - return null if no backend data
    }

    // Convert API data to support multiple time slots per day
    const hours: { [key: string]: { slots: Array<{ startTime: string; endTime: string }>, isWorkingDay: boolean } } = {};
    
    workingHoursData.days.forEach(day => {
      const dayOfWeek = day.dayOfWeek.toString();
      
      if (day.slots.length > 0) {
        hours[dayOfWeek] = {
          slots: day.slots.map(slot => ({
            startTime: slot.startTime,
            endTime: slot.endTime
          })),
          isWorkingDay: true
        };
      } else {
        // No slots = not working day
        hours[dayOfWeek] = {
          slots: [],
          isWorkingDay: false
        };
      }
    });

    return hours;
  }, [workingHoursData]);

  return {
    studioHours,
    isLoading,
    error,
    hasData: !!workingHoursData?.days && !!studioHours
  };
};